import React, { useState } from 'react';
import {
  IAuditoriaCorrelacaoReceitaResultado,
  IAuditoriaCorrelacaoReceitaItem,
} from '../interfaces/IAuditoriaCorrelacaoReceita';
import { formatDate } from '../utils/formatters';

interface Props {
  resultado: IAuditoriaCorrelacaoReceitaResultado;
}

const formatCurrency = (value?: number) =>
  (value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatPct = (value?: number) =>
  `${(value || 0).toFixed(2)}%`;

// COR-297: drill-down a nível de lançamento — o mês anômalo precisa mostrar os lançamentos reais
// de Receita Bruta e de Devoluções/Descontos que compõem o ratio TR/RB divergente.
const LIMITE_LANCAMENTOS_VISIVEIS = 10;

const BlocoLancamentosRelacionados: React.FC<{ item: IAuditoriaCorrelacaoReceitaItem }> = ({ item }) => {
  const [mostrarTodos, setMostrarTodos] = useState(false);
  const lancamentos = item.lancamentosRelacionados || [];

  if (lancamentos.length === 0) return null;

  const visiveis = mostrarTodos ? lancamentos : lancamentos.slice(0, LIMITE_LANCAMENTOS_VISIVEIS);

  return (
    <div className='mt-4'>
      <strong className='fs-8'>Lançamentos Relacionados ({lancamentos.length}):</strong>
      <div className='table-responsive mt-1'>
        <table className='table table-sm' style={{ fontSize: 12 }}>
          <thead>
            <tr>
              <th>Data</th>
              <th>Nº Lançamento</th>
              <th>Conta</th>
              <th>Origem</th>
              <th>Histórico</th>
              <th className='text-end'>Débito</th>
              <th className='text-end'>Crédito</th>
            </tr>
          </thead>
          <tbody>
            {visiveis.map((lanc, idx) => (
              <tr key={`${lanc.numeroLancamento}-${lanc.data}-${idx}`}>
                <td>{formatDate(lanc.data)}</td>
                <td><code>{lanc.numeroLancamento || '-'}</code></td>
                <td><code>{lanc.contaContabil}</code></td>
                <td>{lanc.origem === 'RECEITA' ? 'Receita Bruta' : 'Redutora'}</td>
                <td>{lanc.historico}</td>
                <td className='text-end'>{lanc.valorDebito > 0 ? formatCurrency(lanc.valorDebito) : '-'}</td>
                <td className='text-end'>{lanc.valorCredito > 0 ? formatCurrency(lanc.valorCredito) : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {lancamentos.length > LIMITE_LANCAMENTOS_VISIVEIS && !mostrarTodos && (
        <button
          type='button'
          className='btn btn-link btn-sm p-0 mt-1'
          style={{ fontSize: 12 }}
          onClick={() => setMostrarTodos(true)}
        >
          + {lancamentos.length - LIMITE_LANCAMENTOS_VISIVEIS} lançamento(s) adicional(is)
        </button>
      )}
    </div>
  );
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const cls =
    status === 'ERRO'   ? 'badge-light-danger' :
    status === 'ALERTA' ? 'badge-light-warning' :
    status === 'AVISO'  ? 'badge-light-warning' :
                          'badge-light-success';
  return <span className={`badge ${cls}`}>{status}</span>;
};

const ItemExpandivel: React.FC<{ item: IAuditoriaCorrelacaoReceitaItem }> = ({ item }) => {
  const [expandido, setExpandido] = useState(false);

  return (
    <>
      <tr style={{ cursor: 'pointer' }} onClick={() => setExpandido(!expandido)}>
        <td>
          <span className='fw-bold'>{item.empresaNome}</span>
          <br />
          <span className='text-muted fs-8'>{item.cnpj}</span>
        </td>
        <td className='fw-bold'>{item.anoMes}</td>
        <td>{formatCurrency(item.receitaBruta)}</td>
        <td>{formatCurrency(item.totalRedutoras)}</td>
        <td className='fw-bold'>{formatPct(item.ratio)}</td>
        <td className='text-muted'>{formatPct(item.mediaMovel6m)}</td>
        <td>
          <span className={`fw-bold ${item.status === 'ERRO' ? 'text-danger' : 'text-warning'}`}>
            +{formatPct(item.variacao)}
          </span>
        </td>
        <td><StatusBadge status={item.status} /></td>
        <td className='text-end'>{expandido ? '▲' : '▼'}</td>
      </tr>

      {expandido && (
        <tr>
          <td colSpan={9} className='bg-light-secondary p-4'>
            <div className='mb-3'>
              <strong>Diagnóstico:</strong>
              <p className='text-muted mt-1 mb-2'>{item.mensagemDiagnostico}</p>
            </div>

            {item.status === 'ERRO' && (
              <div className='row mb-4'>
                <div className='col-2 text-center border-end'>
                  <div className='fs-5 fw-bolder text-danger'>{formatCurrency(item.excessoRedutoras)}</div>
                  <div className='text-muted fs-8'>Excesso de Redutoras</div>
                </div>
                <div className='col-2 text-center border-end'>
                  <div className='fs-5 fw-bolder text-danger'>{formatCurrency(item.tributosTotal)}</div>
                  <div className='text-muted fs-8'>PIS/COFINS/IRPJ/CSLL (43,25%)</div>
                </div>
                <div className='col-2 text-center border-end'>
                  <div className='fs-5 fw-bolder text-danger'>{formatCurrency(item.multaArt44)}</div>
                  <div className='text-muted fs-8'>Multa Art. 44 (75%)</div>
                </div>
                <div className='col-2 text-center border-end'>
                  <div className='fs-5 fw-bolder text-warning'>{formatCurrency(item.jurosSelic)}</div>
                  <div className='text-muted fs-8'>Juros SELIC</div>
                </div>
                <div className='col-4 text-center'>
                  <div className='fs-4 fw-bolder text-danger'>{formatCurrency(item.passivoTributario)}</div>
                  <div className='text-muted fs-8'>Passivo Tributário Total</div>
                </div>
              </div>
            )}

            {item.status === 'ERRO' && (
              <div className='alert alert-danger mt-3 py-2'>
                <strong>Income Smoothing — Regularização obrigatória:</strong> Variação do ratio
                Redutoras/Receita Bruta acima de 25% em relação à média dos 6 meses anteriores.
                Indício de simulação de estornos para redução indevida da base de cálculo de
                PIS/COFINS/IRPJ/CSLL. Recolher o passivo e retificar a EFD-Contribuições.
                Lei 9.430/96, Art. 44 I.
              </div>
            )}

            <BlocoLancamentosRelacionados item={item} />
          </td>
        </tr>
      )}
    </>
  );
};

const AuditoriaCorrelacaoReceitaDetalhe: React.FC<Props> = ({ resultado }) => {
  const bannerCor =
    resultado.status === 'ERRO'   ? 'danger' :
    resultado.status === 'ALERTA' ? 'warning' :
    resultado.status === 'AVISO'  ? 'warning' :
                                    'success';

  return (
    <div className='card card-custom card-stretch'>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bolder fs-3 mb-1'>
            Correlação Receita Bruta / Redutoras — Detecção de Income Smoothing
          </span>
          <span className='text-muted mt-1 fw-bold fs-7'>
            Lei 9.718/1998 Art. 3º · Lei 9.430/96 Art. 44 · Lei 12.973/2014 · BCB Série 4189
          </span>
        </h3>
      </div>

      <div className='card-body pt-3'>
        {resultado.status !== 'CONCILIADO' && (
          <div className={`alert alert-${bannerCor} py-3 mb-5`}>
            <strong>{resultado.status}:</strong> {resultado.mensagemDirecionamento}
          </div>
        )}

        <div className='row mb-6'>
          <div className='col-3 text-center border-end'>
            <div className='fs-1 fw-bolder text-danger'>{resultado.totalErros}</div>
            <div className='text-muted'>Erros (&gt; 25%)</div>
            <div className='text-muted fs-8'>Income Smoothing confirmado</div>
          </div>
          <div className='col-3 text-center border-end'>
            <div className='fs-1 fw-bolder text-warning'>{resultado.totalAlertas}</div>
            <div className='text-muted'>Alertas (10,01–25%)</div>
            <div className='text-muted fs-8'>Erro operacional provável</div>
          </div>
          <div className='col-3 text-center border-end'>
            <div className='fs-1 fw-bolder text-warning'>{resultado.totalAvisos}</div>
            <div className='text-muted'>Avisos (≤ 10%)</div>
            <div className='text-muted fs-8'>Variação moderada</div>
          </div>
          <div className='col-3 text-center'>
            <div className='fs-1 fw-bolder text-danger'>{formatCurrency(resultado.passivoTributarioTotal)}</div>
            <div className='text-muted'>Passivo Tributário Total</div>
            <div className='text-muted fs-8'>43,25% + Multa 75% + SELIC</div>
          </div>
        </div>

        {resultado.itens.length === 0 ? (
          <div className='text-success fw-bold fs-5 text-center py-8'>
            ✓ Nenhuma anomalia no ratio Redutoras/Receita Bruta. Situação regular.
          </div>
        ) : (
          <div className='table-responsive'>
            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
              <thead>
                <tr className='fw-bolder text-muted'>
                  <th>Empresa</th>
                  <th>Mês/Ano</th>
                  <th>Receita Bruta</th>
                  <th>Redutoras</th>
                  <th>Ratio</th>
                  <th>µ6m</th>
                  <th>Variação</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {resultado.itens.map((item, i) => (
                  <ItemExpandivel key={i} item={item} />
                ))}
              </tbody>
            </table>
          </div>
        )}

        <details className='mt-6'>
          <summary className='text-muted fw-bold cursor-pointer'>Base Legal</summary>
          <p className='text-muted mt-2 fs-7'>{resultado.fundamentacaoLegal}</p>
        </details>
      </div>
    </div>
  );
};

export default AuditoriaCorrelacaoReceitaDetalhe;
